<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import Badge from '../lib/ui/Badge.svelte';
  import Button from '../lib/ui/Button.svelte';
  import Drawer from '../lib/ui/Drawer.svelte';
  import Notice from '../lib/ui/Notice.svelte';
  import AddRecipientDrawer from './AddRecipientDrawer.svelte';
  import ReportChangesCard from './ReportChangesCard.svelte';
  import ReportSendsCard from './ReportSendsCard.svelte';
  import ScheduleFields from './ScheduleFields.svelte';
  import { ReportDefinitionCreator } from '../lib/report-definition-creator.svelte';
  import { ReportDefinitionEditor } from '../lib/report-definition-editor.svelte';
  import { ReportDefinitionSends } from '../lib/report-definition-sends.svelte';
  import { ReportDefinitionStatus } from '../lib/report-definition-status';
  import { ReportLineDraft } from '../lib/report-line-draft.svelte';
  import { ReportSendsEmptyNote } from '../lib/report-sends-empty-note';
  import type { ReportDefinitionGroup, ReportDefinitionSummary, ReportDefinitionTenant } from '../lib/api';
  import type { ReportDefinitionsBoard } from '../lib/report-definitions-board.svelte';
  import type { ReportLineWriter } from '../lib/report-line-writer';

  /** Sem `definition` é a linha nova do cliente escolhido; com ela, a linha que já existe. */
  interface Props {
    board: ReportDefinitionsBoard;
    group: ReportDefinitionGroup;
    tenant: ReportDefinitionTenant;
    definition: ReportDefinitionSummary | null;
    onClose: () => void;
  }

  let { board, group, tenant, definition, onClose }: Props = $props();

  const draft = untrack(
    () =>
      new ReportLineDraft({
        schedule: definition?.schedule ?? null,
        contacts: definition?.recipients.map(({ hqCustomerId, contact }) => ({ hqCustomerId, contact })) ?? [],
      }),
  );
  const editor = untrack(() => {
    const opened = definition;
    if (!opened) return null;
    return new ReportDefinitionEditor(board, () => definition ?? opened);
  });
  const sendsLoader = untrack(() => (definition ? new ReportDefinitionSends(board) : null));
  const writer: ReportLineWriter = untrack(() => editor ?? new ReportDefinitionCreator({ board, group, tenant }));
  const busy = $derived(writer.busy);
  const status = $derived(definition ? ReportDefinitionStatus.of(definition.status) : null);
  let addingContact = $state(false);
  let confirmingRemoval = $state(false);
  let confirmingContact = $state<string | null>(null);

  onMount(() => {
    void editor?.loadChanges();
    if (definition) void sendsLoader?.load(definition);
  });

  async function save(): Promise<void> {
    if (await writer.save(draft)) onClose();
  }

  async function remove(): Promise<void> {
    if (await editor?.remove()) onClose();
  }
</script>

{#if addingContact}
  <AddRecipientDrawer
    eyebrow="{group.name} · {tenant.name}"
    {draft}
    error={board.error}
    onError={(err) => board.fail(err)}
    onClose={() => (addingContact = false)}
  />
{:else}
  <Drawer eyebrow={group.name} title={tenant.name} {onClose}>
    {#if board.error}
      <Notice tone="danger">{board.error}</Notice>
    {/if}
    {#if definition && editor && status}
      <div class="status">
        <Badge tone={status.tone} dot>{status.label}</Badge>
        {#if definition.status === 'paused'}
          <span class="sub">Nada sai até ligar de novo</span>
          <Button size="sm" variant="primary" disabled={busy} onclick={() => editor.resume()}>Ligar</Button>
        {:else}
          <span class="sub">Sai no horário abaixo</span>
          <Button size="sm" disabled={busy} onclick={() => editor.pause()}>Pausar</Button>
        {/if}
      </div>
    {/if}
    <fieldset class="box">
      <legend>Quando sai</legend>
      <ScheduleFields draft={draft.schedule} />
    </fieldset>
    <fieldset class="box">
      <legend>Quem recebe · {draft.contacts.length}</legend>
      <div class="rows">
        {#each draft.contacts as listed (listed.hqCustomerId)}
          <div class="row">
            <span class="who">
              <span class="name">{listed.contact?.name ?? 'Contato fora do HQ'}</span>
              <span class="sub mono">{listed.contact?.identification ?? 'Sem telefone'}</span>
            </span>
            {#if confirmingContact === listed.hqCustomerId}
              <span class="actions">
                <Button size="sm" onclick={() => (confirmingContact = null)}>Cancelar</Button>
                <Button size="sm" variant="confirmDanger" onclick={() => draft.remove(listed.hqCustomerId)}>Remover</Button>
              </span>
            {:else}
              <Button size="sm" variant="danger" onclick={() => (confirmingContact = listed.hqCustomerId)}>Remover</Button>
            {/if}
          </div>
        {:else}
          <Notice tone="warning">Adicione ao menos um contato</Notice>
        {/each}
      </div>
      <div><Button size="sm" onclick={() => (addingContact = true)}>+ Adicionar contato</Button></div>
    </fieldset>
    {#if definition && sendsLoader}
      <ReportSendsCard
        loader={sendsLoader}
        emptyNote={ReportSendsEmptyNote.of(definition, new Date())}
        onOpen={() => board.openSends(definition)}
      />
    {/if}
    {#if editor}
      <ReportChangesCard changes={editor.changes} />
    {/if}
    {#snippet footer()}
      {#if confirmingRemoval}
        <span class="sub confirm">Tirar {tenant.name} do relatório? Nada mais é enviado.</span>
        <Button onclick={() => (confirmingRemoval = false)}>Cancelar</Button>
        <Button variant="confirmDanger" disabled={busy} onclick={remove}>Tirar do relatório</Button>
      {:else}
        {#if editor}
          <Button variant="danger" disabled={busy} onclick={() => (confirmingRemoval = true)}>Tirar do relatório</Button>
        {/if}
        <Button onclick={onClose}>Cancelar</Button>
        <Button variant="primary" disabled={!draft.isComplete || busy} onclick={save}>Salvar</Button>
      {/if}
    {/snippet}
  </Drawer>
{/if}

<style>
  .status {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }
  .status > :global(:last-child) {
    margin-left: auto;
  }
  .box {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    padding: var(--space-3) var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  legend {
    font-size: 12px;
    color: var(--text-secondary);
    padding: 0 var(--space-1);
  }
  .rows {
    display: flex;
    flex-direction: column;
  }
  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-2) 0;
    border-top: 1px solid var(--border-subtle);
  }
  .row:first-child {
    border-top: none;
  }
  .actions {
    display: flex;
    gap: var(--space-2);
  }
  .who {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .name {
    color: var(--text-primary);
    font-weight: 500;
  }
  .sub {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .confirm {
    margin-right: auto;
    align-self: center;
  }
</style>
